import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';

interface UserInfo {
  id: number;
  email: string;
  approved: number;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
}

interface UserState {
  user: UserInfo | null;
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  user: null,
  loading: false,
  error: null,
};

// Async thunk for fetching user information
export const fetchUserInfo = createAsyncThunk<UserInfo, { token: string }, { rejectValue: string }>(
  'user/fetchUserInfo',
  async ({ token }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${import.meta.env.VITE_AUTH_BASE_URL}/api/v1/auth/user/client-info`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data?.client?.[0];
      return data;
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message);
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    resetUserState: (state) => {
      state.user = null;
      state.loading = false;
      state.error = null;
      localStorage.removeItem('user');
    },
    setUserFromCache: (state, action: PayloadAction<UserInfo>) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserInfo.fulfilled, (state, action: PayloadAction<UserInfo>) => {
        state.loading = false;
        state.user = action.payload;
        localStorage.setItem('user', JSON.stringify(action.payload));
      })
      .addCase(fetchUserInfo.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || null;
      });
  },
});

export const { resetUserState, setUserFromCache } = userSlice.actions;
export default userSlice.reducer;
